import { rem } from 'polished';
import styled, { css, RuleSet } from 'styled-components';

import { spacingStyles, StyledSpacingProps } from '../../styles';
import {
  borderRadius,
  breakpoints,
  fontSizes,
  fontWeights,
  spacing,
  zIndexes,
} from '../../theme';
import colors from '../../theme/constants/colors';
import { hexWithOpacity } from '../../utils';
import IconButton from '../IconButton/IconButton';

import { ModalVariant } from './types';

export const ContainerWithOverlay = styled.div`
  position: fixed;
  z-index: ${zIndexes.modal};
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  background: ${hexWithOpacity(colors.navy, 0.8)};
`;

// header

export const ModalTitle = styled.span`
  display: inline-block;
  font-weight: ${fontWeights.medium};
  font-size: ${rem(20)};
  line-height: ${rem(28)};
  margin-right: ${spacing['16x']};
`;

export const ModalDescription = styled.span`
  display: inline-block;
  font-weight: ${fontWeights.regular};
  font-size: ${fontSizes.l};
  line-height: ${rem(20)};
`;

export const ModalHeaderButton = styled(IconButton)`
  position: absolute;
  top: ${spacing['8x']};
  right: ${spacing['8x']};
`;

export const ModalHeaderRoot = styled.header`
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  padding: ${spacing['16x']};
`;

// content
export const ModalContent = styled.div<StyledSpacingProps>`
  box-sizing: border-box;
  padding: ${spacing['16x']};
  overflow: auto;
  ${spacingStyles}
`;

// footer
export const ModalFooterRoot = styled.footer`
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  padding: ${spacing['16x']};
  gap: ${spacing['8x']};

  &:empty {
    display: none;
  }
`;

export const ModalButtonGroup = styled.div<StyledSpacingProps>`
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  align-items: center;
  box-sizing: border-box;
  gap: ${spacing['8x']};

  &:empty {
    display: none;
  }

  ${spacingStyles}
`;

const styleOverridesByVariant: Record<ModalVariant, RuleSet<object>> = {
  info: css`
    width: ${rem(960)};
  `,
  form: css`
    width: ${rem(512)};
  `,
  complex: css`
    width: ${rem(960)};
  `,
};

const modalAreaBorder = `1px solid ${colors.sand}`;

export const ModalCard = styled.div<{ $variant: ModalVariant }>`
  position: relative;
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background-color: ${colors.white};
  border-radius: ${borderRadius['5x']};
  box-shadow: 4px 4px 20px ${hexWithOpacity(colors.navy, 0.1)};

  min-width: 300px;
  max-width: 80vw;
  max-height: 80vh;

  @media (width < ${breakpoints.m}) {
    max-width: 90vw;
    max-height: 90vh;
  }

  ${ModalHeaderRoot} + ${ModalContent},
  ${ModalContent} + ${ModalFooterRoot},
  ${ModalContent} + ${ModalHeaderRoot} {
    border-top: ${modalAreaBorder};
  }

  ${({ $variant }) => styleOverridesByVariant[$variant]}
`;
